import React, {useState, useEffect, useMemo} from 'react'
import usePostApi from '../custom_hooks/usePostApi'
import {UseToast} from '../useToast'
import {useParams, Link} from 'react-router-dom'
import * as pdfjsLib from 'pdfjs-dist'
import './pdfWorker'
import {KTIcon} from '../../../_metronic/helpers'
import {PageTitle} from '../../../_metronic/layout/core'
import DateFormatter from '../DateFormatter'

type Props = {}

type DocDetails = {
  doc_title: string
  expiry_ts: null | string
  send_in_order: number
  enable_tour: number
  auto_remainder: number
  created_ts: string | null
  doc_metadata: {document_url: string}
  sign_coordinates: any[]
}

const extractImagesFromPDFUrl = async (pdfUrl: string): Promise<string[]> => {
  try {
    const pdf = await pdfjsLib.getDocument(pdfUrl).promise
    const images: string[] = []

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const viewport = page.getViewport({scale: 1.5})

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')!
      canvas.width = viewport.width
      canvas.height = viewport.height

      await page.render({canvasContext: context, viewport}).promise
      const imgData = canvas.toDataURL('image/png')
      images.push(imgData)
    }

    return images
  } catch (error) {
    console.error('Error extracting images:', error)
    return []
  }
}

const DocDetails = (props: Props) => {
  const {execute, loading} = usePostApi()
  const {req_id, id} = useParams()
  const [docDetails, setDocDetails] = useState<Partial<DocDetails>>({})

  const getDocuments = async () => {
    try {
      const url = '/document/request/get'
      const formData = {
        request_id: req_id,
      }

      const response = await execute(url, 'POST', formData)
      setDocDetails(response.data)
    } catch (error: any) {
      console.error('Error:', error)
      UseToast(
        error.response?.data?.status_message,
        {
          position: 'bottom-right',
          theme: 'colored',
          autoClose: true,
        },
        'error'
      )
    }
  }

  useEffect(() => {
    getDocuments()
  }, [])

  const [pdfImgs, setPdfImgs]: any = useState([])
  const [currentPage, setCurrentPage] = useState(0)

  const [file, setFile] = useState<string | null>(null)

  const getFileWithKey = async () => {
    try {
      const url = '/get/signed-url'
      const formData = docDetails.doc_metadata

      const response = await execute(url, 'POST', formData)
      setFile(response)
    } catch (error: any) {
      console.error('Error:', error)
      UseToast(
        error.response?.data?.status_message,
        {
          position: 'bottom-right',
          theme: 'colored',
          autoClose: true,
        },
        'error'
      )
    }
  }

  useEffect(() => {
    if (docDetails?.doc_metadata) {
      getFileWithKey()
    }
  }, [docDetails])

  const getPdfImgs = async () => {
    if (file) {
      const images = await extractImagesFromPDFUrl(file)
      setPdfImgs(images)
    }
  }

  useEffect(() => {
    getPdfImgs()
  }, [file])

  const [pendingRecipients, setPendingRecipients] = useState({})

  useEffect(() => {
    if (docDetails.sign_coordinates) {
      const recipients: any = {}
      const pendingSigners = docDetails.sign_coordinates.forEach((e) => {
        recipients[e.assignedTo] = recipients[e.assignedTo] ? recipients[e.assignedTo] + 1 : 1
      })
      setPendingRecipients(recipients)
    }
  }, [docDetails.sign_coordinates])

  const getPdfView = () => {
    if (pdfImgs?.length > 0) {
      return (
        <div className='d-flex align-items-start' style={{minHeight: '100%'}}>
          {/* Sidebar Thumbnails */}
          <div
            className='card d-flex flex-column align-items-center w-25 p-5 align-self-stretch gap-3'
            style={{
              overflowY: 'auto',
              maxHeight: '100vh',
            }}
          >
            {pdfImgs.map((img: string, index: number) => {
              const uniqueKey = `img-${index}`
              return (
                <div key={uniqueKey} onClick={() => setCurrentPage(index)}>
                  <img
                    src={img}
                    alt={`thumbnail ${index}`}
                    className={`w-100 ${currentPage === index ? 'active' : ''}`}
                    style={{
                      border: currentPage === index ? '2px solid #007bff' : '1px solid #ccc',
                      borderRadius: 4,
                      transition: 'border 0.3s',
                    }}
                  />
                  <p className='text-center fw-normal mt-2 mb-0' style={{fontSize: 14}}>
                    Page {index + 1}
                  </p>
                </div>
              )
            })}
          </div>

          {/* Main Image Viewer */}
          <div className='w-75 d-flex justify-content-center align-items-center p-0'>
            <img
              src={pdfImgs[currentPage]}
              alt={`pdf page ${currentPage + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
                borderRadius: 8,
              }}
            />
          </div>

          <div className='w-25 card align-self-stretch p-3'>
            <h3 className='fw-semibold'>Pending Recipients</h3>
            {Object.entries(pendingRecipients).map(([key, value]: any) => (
              <div
                key={key}
                className='mb-0 fw-semibold fs-6 bg-light-danger p-2 rounded mb-2 d-flex justify-content-between align-items-center'
              >
                <p className='mb-0'>{key}</p>
                <span>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )
    } else {
      return (
        <div className='card h-250px'>
          <div className='m-auto d-flex flex-column align-items-center'>
            <div className='spinner-border spinner-primary mr-15'></div>
          </div>
        </div>
      )
    }
  }

  const documents = [
    {
      title: `(${id})`,
      path: `/${id}/documents`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: `Documents`,
      path: `/${id}/documents`,
      isSeparator: true,
      isActive: false,
    },
    {
      title: 'Documents',
      path: `/${id}/documents`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: `Documents`,
      path: `/${id}/documents`,
      isSeparator: true,
      isActive: false,
    },
    {
      title: docDetails?.doc_title || '',
      path: `/${id}/documents/${req_id}`,
      isSeparator: false,
      isActive: false,
    },
  ]

  if (loading) {
    return (
      <div className='card h-250px'>
        <div className='m-auto d-flex flex-column align-items-center'>
          <div className='spinner-border spinner-primary mr-15'></div>
        </div>
      </div>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={documents}>Document Details</PageTitle>
      <div>
        <div className='d-flex flex-wrap flex-stack mb-6'>
          <div>
            <h3 className='fs-3 fw-semibold my-2'>Document Info</h3>
          </div>
          <div className='d-flex'>
            <Link className='btn btn-sm btn-light-primary ms-4 fw-normal' to={`/${id}/documents`}>
              <KTIcon iconName='arrow-left' className='fs-3' />
              Back
            </Link>
          </div>
        </div>

        <div className='card mb-4 mb-xl-6'>
          {docDetails ? (
            <div className='card-body min-h-150px row p-5'>
              <div className='d-flex align-items-start mb-5'>
                <span className='text-gray-800 text-hover-primary default-cursor fs-2 fw-semibold me-1'>
                  {docDetails?.doc_title}
                </span>
              </div>
              <div className='row col-12 col-md-6 col-lg-5'>
                <div>
                  <div className='row mb-3 d-flex align-items-start'>
                    <label className='col-lg-4 col-6 fw-semibold text-muted'>Send in Order</label>
                    <div className='col-lg-8 col-6'>
                      <span className='fs-6 text-dark text-hover-primary default-cursor'>
                        {docDetails?.send_in_order === 1 ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className='row mb-3 d-flex align-items-start'>
                    <label className='col-lg-4 col-6 fw-semibold text-muted'>Enable Tour</label>
                    <div className='col-lg-8 col-6'>
                      <span className='fs-6 text-dark text-hover-primary default-cursor'>
                        {docDetails?.enable_tour === 1 ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className='row mb-3 d-flex align-items-start'>
                    <label className='col-lg-4 col-6 fw-semibold text-muted'>Auto Remainder</label>
                    <div className='col-lg-8 col-6'>
                      <span className='fs-6 text-dark text-hover-primary default-cursor'>
                        {docDetails?.auto_remainder === 1 ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className='row col-12 col-md-6 col-lg-7'>
                <div>
                  <div className='row mb-3 d-flex align-items-start'>
                    <label className='col-lg-4 col-6 fw-semibold text-muted'>
                      Time To Complete
                    </label>
                    <div className='col-lg-8 col-6'>
                      <span className='fs-6 text-dark text-hover-primary default-cursor'>
                        {docDetails?.expiry_ts ? DateFormatter(docDetails.expiry_ts) : '-'}
                      </span>
                    </div>
                  </div>
                  <div className='row mb-3 d-flex align-items-start'>
                    <label className='col-lg-4 col-6 fw-semibold text-muted'>Status</label>
                    <div className='col-lg-8 col-6'>
                      <span className='fs-6 text-dark text-hover-primary default-cursor'>
                        Active
                      </span>
                    </div>
                  </div>
                  <div className='row mb-3 d-flex align-items-start'>
                    <label className='col-lg-4 col-6 fw-semibold text-muted'>Created At</label>
                    <div className='col-lg-8 col-6'>
                      <span className='fs-6 text-dark text-hover-primary default-cursor'>
                        {docDetails?.created_ts ? DateFormatter(docDetails?.created_ts) : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className='row col-12 col-md-6 col-lg-7 d-flex flex-column justify-content-start'> */}
            </div>
          ) : (
            <div className='d-flex flex-column justify-content-center align-items-center h-150px'>
              <div>
                <h3 className='fs-3 fw-normal'>Not found</h3>
                <button className='btn btn-sm btn-light-primary' onClick={getDocuments}>
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>

        {getPdfView()}
      </div>
    </>
  )
}

export {DocDetails}
